<script setup>
  import { computed, ref, onMounted, watch } from "vue";
  import CriterionCard from "./CriterionCard.vue";
  import WeightingBar from "./WeightingBar.vue";
  import RubricPatternLibrary from "./RubricPatternLibrary.vue";
  import { getAcceptedFileFormats } from '@/services/fileService';
  import { validateReferenceFile } from '@/pages/dataset/services/eval/evalBuilderValidationService';
  import { useToast } from '@/composables/use-toast.js';
  import { ENVIRONMENT_PRESET_CATALOG, getEnvironmentPresetById } from '@/pages/eval/eval-builder/data/environmentPresets';
  import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
  
  const { addToast } = useToast();
  const fileInputRef = ref(null);
  const acceptedFormats = ref([]);
  const acceptedExtensions = ref('');
  
  const previewTaskExpanded = ref(true);
  const previewAnswerExpanded = ref(true);
  const previewReferencesExpanded = ref(true);
  const previewCriteriaExpanded = ref(true);
  const previewEnvironmentExpanded = ref(true);
  const pendingReferenceAction = ref('append');
    
  const props = defineProps({
    task: Object,
    canDelete: {
      type: Boolean,
      default: true
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    isFirstTask: {
      type: Boolean,
      default: false
    },
    isExampleMode: {
      type: Boolean,
      default: false
    },
    isLastTask: {
      type: Boolean,
      default: false
    },
    isAgentic: {
      type: Boolean,
      default: false
    }
  });
  
  const emit = defineEmits(['updateTask', 'deleteTask', 'previousTask', 'nextTask']);
  
  const activeTab = ref("task");
  const showRubricLibrary = ref(false);
  const tabs = computed(() => (
    props.isAgentic
      ? ['task', 'criterion', 'answer', 'environment', 'preview']
      : ['task', 'criterion', 'answer', 'preview']
  ));

  const TAB_LABELS = {
    task: 'Task',
    criterion: 'Grading Rubric',
    answer: 'Golden Answer',
    environment: 'Environment',
    preview: 'Preview',
  };

  const getTabLabel = (tab) => TAB_LABELS[tab] || tab;

  const containerMode = ref('simple');
  const environmentSectionState = ref({
    presets: true,
    container: false,
    resources: false,
    timeouts: false,
  });
  const DOCKERFILE_MAX_LENGTH = 10000;

  const toggleEnvironmentSection = (section) => {
    environmentSectionState.value[section] = !environmentSectionState.value[section];
  };

  const isEnvironmentSectionOpen = (section) => Boolean(environmentSectionState.value[section]);

  const initContainerMode = () => {
    const df = props.task.environment?.dockerfile;
    containerMode.value = (df && df.trim()) ? 'dockerfile' : 'simple';
  };

  watch(() => props.task?.id, () => { initContainerMode(); }, { immediate: true });

  function generateDockerfileFromSimple() {
    const env = getEnvironment();
    const lines = [`FROM ${env.base_image || 'ubuntu:24.04'}`, 'WORKDIR /app'];
    if (env.system_packages?.length) {
      lines.push(`RUN apt-get update && apt-get install -y ${env.system_packages.join(' ')}`);
    }
    if (env.python_packages?.length) {
      lines.push(`RUN pip install ${env.python_packages.join(' ')}`);
    }
    if ((props.task.references || []).length) {
      lines.push('');
      lines.push('COPY refs/ /app/refs/');
    }
    return lines.join('\n') + '\n';
  }

  function switchContainerMode(mode) {
    if (props.isEditing || props.isExampleMode) return;
    if (mode === containerMode.value) return;

    if (mode === 'dockerfile') {
      const dockerfile = props.task.environment?.dockerfile?.trim()
        ? props.task.environment.dockerfile
        : generateDockerfileFromSimple();
      updateEnvironmentField('dockerfile', dockerfile);
    } else {
      updateEnvironmentField('dockerfile', '');
    }
    containerMode.value = mode;
  }

  function analyzeDockerfile(content) {
    const warnings = [];
    if (!content || !content.trim()) return warnings;

    const hasFrom = content.split('\n').some(l => l.trim().toUpperCase().startsWith('FROM'));
    if (!hasFrom) {
      warnings.push({ level: 'error', message: 'Dockerfile must contain a FROM instruction.' });
    }

    if (content.length > DOCKERFILE_MAX_LENGTH) {
      warnings.push({ level: 'error', message: `Dockerfile exceeds ${DOCKERFILE_MAX_LENGTH.toLocaleString()} character limit.` });
    }

    if (/ENV\s+(PASSWORD|SECRET|API_KEY|TOKEN|PRIVATE_KEY|AWS_SECRET)/i.test(content)) {
      warnings.push({ level: 'warning', message: 'Avoid hardcoding credentials. Use build args or runtime secrets.' });
    }

    if (/curl.*\|\s*(bash|sh)/i.test(content) || /wget.*\|\s*(bash|sh)/i.test(content)) {
      warnings.push({ level: 'warning', message: 'Piping remote scripts to shell detected. Ensure the source is trusted.' });
    }

    if (/--privileged/i.test(content)) {
      warnings.push({ level: 'warning', message: 'Privileged mode detected. This may be restricted in production.' });
    }

    return warnings;
  }

  const dockerfileWarnings = computed(() => {
    if (containerMode.value !== 'dockerfile') return [];
    return analyzeDockerfile(props.task.environment?.dockerfile || '');
  });

  const hasDockerfileErrors = computed(() =>
    dockerfileWarnings.value.some(w => w.level === 'error')
  );

  const defaultEnvironment = () => ({
    base_image: 'ubuntu:24.04',
    system_packages: [],
    python_packages: [],
    dockerfile: '',
    resources: {
      cpus: 1,
      memory_mb: 2048,
      storage_mb: 10240,
      gpus: 0,
    },
    timeouts: {
      agent_sec: 900,
      verifier_sec: 900,
      build_sec: 600,
    },
  });

  const defaultMetadata = () => ({
    difficulty: 'medium',
    category: '',
    tags: [],
    environment_profile: null,
    expert_time_estimate_min: null,
    junior_time_estimate_min: null,
  });

  const tagInput = ref('');
  const systemPackageInput = ref('');
  const pythonPackageInput = ref('');
  const environmentPresetDomainFilter = ref('all');

  const commonBaseImages = [
    'ubuntu:24.04',
    'python:3.13-slim',
    'python:3.12-slim',
    'python:3.11-slim',
    'node:20-slim',
  ];

  const RESOURCE_PRESETS = {
    standard: { cpus: 1, memory_mb: 2048, storage_mb: 10240, gpus: 0 },
    boosted: { cpus: 2, memory_mb: 4096, storage_mb: 10240, gpus: 0 },
    heavy: { cpus: 4, memory_mb: 8192, storage_mb: 10240, gpus: 0 },
  };

  const environmentPresetDomains = computed(() => (
    ['all', ...new Set(ENVIRONMENT_PRESET_CATALOG.map(preset => preset.domain))]
  ));

  const filteredEnvironmentPresets = computed(() => {
    if (environmentPresetDomainFilter.value === 'all') return ENVIRONMENT_PRESET_CATALOG;
    return ENVIRONMENT_PRESET_CATALOG.filter(preset => preset.domain === environmentPresetDomainFilter.value);
  });

  const activeEnvironmentProfile = computed(() => props.task.metadata?.environment_profile || null);
  const activeEnvironmentPresetId = computed(() => activeEnvironmentProfile.value?.preset_id || '');

  const normalizeEnvironmentPresetMode = (preset) => (
    preset?.mode || (preset?.environment?.dockerfile?.trim() ? 'dockerfile' : 'simple')
  );

  const formatEnvironmentPresetDomain = (domain) => {
    if (!domain) return 'Unknown';
    if (domain === 'all') return 'All';
    return domain.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  };

  const formatEnvironmentPresetList = (items) => (
    Array.isArray(items) && items.length ? items.join(', ') : 'None'
  );

  const isEnvironmentPresetActive = (presetId) => activeEnvironmentPresetId.value === presetId;

  function buildEnvironmentFromPreset(preset) {
    const defaults = defaultEnvironment();
    const presetEnvironment = preset?.environment || {};

    return {
      ...defaults,
      ...presetEnvironment,
      system_packages: [...(presetEnvironment.system_packages || [])],
      python_packages: [...(presetEnvironment.python_packages || [])],
      dockerfile: presetEnvironment.dockerfile || '',
      resources: { ...defaults.resources, ...(presetEnvironment.resources || {}) },
      timeouts: { ...defaults.timeouts, ...(presetEnvironment.timeouts || {}) },
    };
  }

  function buildEnvironmentProfileFromPreset(preset) {
    return {
      preset_id: preset.id,
      preset_name: preset.name,
      env_version: preset.env_version,
      domain: preset.domain,
      data_modality: [...(preset.data_modality || [])],
      tooling: [...(preset.tooling || [])],
      network_policy: preset.network_policy || 'offline',
      container_mode: normalizeEnvironmentPresetMode(preset),
      source_repo: preset.source_repo || 'builtin/frontend',
      source_ref: preset.source_ref || 'v1',
      customized: false,
    };
  }

  function applyEnvironmentPreset(presetId) {
    if (props.isEditing || props.isExampleMode) return;

    const preset = getEnvironmentPresetById(presetId);
    if (!preset) return;

    const environment = buildEnvironmentFromPreset(preset);

    if (props.isAgentic) {
      const memoryMb = environment.resources?.memory_mb ?? defaultEnvironment().resources.memory_mb;
      const { fits, totalSizeBytes, memoryLimitBytes } = referencesFitMemoryLimit(props.task.references || [], memoryMb);
      if (!fits) {
        addToast(
          `Preset memory ${formatFileSize(memoryLimitBytes)} is below attached reference size (${formatFileSize(totalSizeBytes)}).`,
          'error'
        );
        return;
      }
    }

    const metadata = {
      ...defaultMetadata(),
      ...(props.task.metadata || {}),
      environment_profile: buildEnvironmentProfileFromPreset(preset),
    };

    emit('updateTask', { ...props.task, environment, metadata });
    containerMode.value = normalizeEnvironmentPresetMode(preset);
    environmentSectionState.value.container = true;
    addToast(`Applied environment preset: ${preset.name}`, 'success');
  }

  function clearEnvironmentPresetProfile() {
    if (props.isEditing || props.isExampleMode) return;
    if (!props.task.metadata?.environment_profile) return;

    const metadata = {
      ...defaultMetadata(),
      ...(props.task.metadata || {}),
      environment_profile: null,
    };

    emit('updateTask', { ...props.task, metadata });
  }

  const activeResourcePreset = computed(() => {
    const res = props.task.environment?.resources;
    if (!res) return 'standard';
    for (const [key, preset] of Object.entries(RESOURCE_PRESETS)) {
      if (preset.cpus === res.cpus && preset.memory_mb === res.memory_mb &&
          preset.storage_mb === res.storage_mb && preset.gpus === res.gpus) {
        return key;
      }
    }
    return null;
  });

  const isCustomBaseImage = computed(() => {
    const img = props.task.environment?.base_image;
    return img !== undefined && img !== '' && !commonBaseImages.includes(img);
  });

  const referencesCount = computed(() => (props.task.references || []).length);
  const hasReferences = computed(() => referencesCount.value > 0);
  const referenceTitle = computed(() => props.isAgentic ? 'Reference Files' : 'Reference File');
  const referenceInputAccept = computed(() => (props.isAgentic ? '' : acceptedExtensions.value));
  const referenceHelpText = computed(() => (
    props.isAgentic
      ? 'Attach one or more files. Any file type is supported for agentic tasks. Total attached size must fit container memory.'
      : 'Attach one reference file. Supported formats follow the accepted file list.'
  ));
  const referenceActionLabel = computed(() => {
    if (!hasReferences.value) {
      return props.isAgentic ? 'Add Reference Files' : 'Attach Reference File';
    }
    return props.isAgentic ? 'Replace All Files' : 'Replace Reference File';
  });

  const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.ico', '.tiff', '.tif'];
  const GRADER_LABELS = {
    'llm-judge': 'LLM-as-a-Judge',
    ExactMatch: 'Exact Match',
  };

  const resolveCriterionGraderType = (criterion = {}) => {
    if (criterion.graderType === 'ExactMatch' || criterion.grader_type === 'ExactMatch') {
      return 'ExactMatch';
    }
    if (criterion.graderType === 'llm-judge' || criterion.grader_type === 'llm-judge') {
      return 'llm-judge';
    }
    if (criterion.type === 'exact_match' || criterion.type === 'exact') {
      return 'ExactMatch';
    }
    return 'llm-judge';
  };

  const formatCriterionGraderType = (criterion = {}) => (
    GRADER_LABELS[resolveCriterionGraderType(criterion)] || 'LLM-as-a-Judge'
  );

  const criteriaTooltipText = computed(() => (
    props.isAgentic
      ? 'Define objective rubric criteria. Each criterion can be scored by LLM-as-a-Judge or Exact Match. At least one criterion is required.'
      : 'Define objective rubric criteria used to score this task. At least one criterion is required before creating the eval.'
  ));
  const criteriaDescriptionText = computed(() => (
    props.isAgentic
      ? 'Add criteria to score responses. You can mix LLM-as-a-Judge and Exact Match within the same task.'
      : 'Add at least one grading criterion. Clear, granular rubric criteria improve scoring reliability.'
  ));
  const answerTabTitle = computed(() => 'Golden Answer');
  const answerTooltipText = computed(() => (
    props.isAgentic
      ? 'Provide the canonical golden answer that accompanies your grading rubric.'
      : 'Provide the canonical golden answer used as the reference alongside your grading rubric.'
  ));
  const answerDescriptionText = computed(() => (
    props.isAgentic
      ? 'Use this as the canonical reference solution paired with your grading rubric and expected end state.'
      : 'Provide the canonical reference answer paired with your grading rubric.'
  ));

  const normalizeCriterionForTask = (criterion = {}) => ({
    ...criterion,
    graderType: resolveCriterionGraderType(criterion),
  });

  const normalizeCriteriaForTask = (criteria = []) => criteria.map(normalizeCriterionForTask);

  const FILE_TYPE_LABELS = {
    'application/json': 'JSON',
    'application/pdf': 'PDF',
    'application/zip': 'ZIP archive',
    'application/x-zip-compressed': 'ZIP archive',
    'text/plain': 'Text',
    'text/csv': 'CSV',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel workbook',
    'application/vnd.ms-excel': 'Excel workbook',
  };
  const EXTENSION_TYPE_LABELS = {
    ipynb: 'Jupyter notebook',
    json: 'JSON',
    pdf: 'PDF',
    csv: 'CSV',
    txt: 'Text',
    md: 'Markdown',
    parquet: 'Parquet',
    xlsx: 'Excel workbook',
    xls: 'Excel workbook',
    docx: 'Word document',
    doc: 'Word document',
    pptx: 'PowerPoint',
    ppt: 'PowerPoint',
    py: 'Python',
    js: 'JavaScript',
    ts: 'TypeScript',
    html: 'HTML',
    xml: 'XML',
    yaml: 'YAML',
    yml: 'YAML',
  };
  
  function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function getReferencesTotalSize(references = []) {
    return references.reduce((sum, ref) => sum + (Number(ref?.size) || 0), 0);
  }

  function getMemoryLimitBytes(memoryMbOverride = null) {
    const currentMemoryMb = memoryMbOverride ?? getEnvironment().resources?.memory_mb ?? defaultEnvironment().resources.memory_mb;
    const memoryMb = Number(currentMemoryMb);
    if (!Number.isFinite(memoryMb) || memoryMb <= 0) return 0;
    return Math.floor(memoryMb * 1024 * 1024);
  }

  function referencesFitMemoryLimit(references = [], memoryMbOverride = null) {
    const totalSizeBytes = getReferencesTotalSize(references);
    const memoryLimitBytes = getMemoryLimitBytes(memoryMbOverride);
    return {
      fits: totalSizeBytes <= memoryLimitBytes,
      totalSizeBytes,
      memoryLimitBytes
    };
  }
  
  function dedupeReferencesByFilename(references = []) {
    const map = new Map();
    references.forEach((ref) => {
      const key = ref?.filename;
      if (!key) return;
      map.set(key, ref);
    });
    return Array.from(map.values());
  }

  function buildReferenceFromFile(file) {
    return {
      id: crypto.randomUUID(),
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
      file
    };
  }

  function clearFileInput() {
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
  }

  function isImageReference(reference) {
    if (reference?.mimeType?.startsWith('image/')) return true;
    const filename = reference?.filename || '';
    const lower = filename.toLowerCase();
    return IMAGE_EXTENSIONS.some((extension) => lower.endsWith(extension));
  }

  function getFileExtension(reference) {
    const filename = reference?.filename || '';
    const lastDot = filename.lastIndexOf('.');
    if (lastDot < 0 || lastDot === filename.length - 1) return '';
    return filename.slice(lastDot + 1).toLowerCase();
  }

  function formatReferenceType(reference) {
    const mimeType = reference?.mimeType || '';
    const extension = getFileExtension(reference);

    if (mimeType && FILE_TYPE_LABELS[mimeType]) {
      return extension ? `${FILE_TYPE_LABELS[mimeType]} (.${extension})` : FILE_TYPE_LABELS[mimeType];
    }

    if (mimeType && mimeType.startsWith('image/')) {
      return extension ? `Image (.${extension})` : 'Image';
    }

    if (extension && EXTENSION_TYPE_LABELS[extension]) {
      return `${EXTENSION_TYPE_LABELS[extension]} (.${extension})`;
    }

    if (mimeType) {
      const friendly = mimeType.split('/').pop()?.replace(/[-_]+/g, ' ') || 'File';
      return extension ? `${friendly} (.${extension})` : friendly;
    }

    if (extension) {
      return `.${extension} file`;
    }

    return 'Unknown file type';
  }

  function getMetadataSummary(task) {
    const metadata = {
      ...defaultMetadata(),
      ...(task?.metadata || {})
    };

    const tags = (metadata.tags || []).length > 0 ? metadata.tags.join(', ') : 'None';
    const category = metadata.category || 'Unspecified';
    const expertTime = metadata.expert_time_estimate_min ? `${metadata.expert_time_estimate_min} min` : '—';
    const juniorTime = metadata.junior_time_estimate_min ? `${metadata.junior_time_estimate_min} min` : '—';

    return {
      difficulty: metadata.difficulty || 'medium',
      category,
      tags,
      expertTime,
      juniorTime
    };
  }

  function getFilesToProcess(selectedFiles) {
    if (props.isAgentic) return { files: selectedFiles };
    const single = [selectedFiles[0]];
    if (selectedFiles.length > 1) {
      addToast('Q&A evals support one reference file per task. Using the first selected file.', 'info');
    }
    return { files: single };
  }

  async function validateFilesAsync(files) {
    const validFiles = [];
    const errors = [];
    for (const file of files) {
      if (props.isAgentic) {
        validFiles.push(file);
        continue;
      }
      try {
        await validateReferenceFile(file);
        validFiles.push(file);
      } catch (error) {
        errors.push(`${file.name}: ${error.message}`);
      }
    }
    return { validFiles, errors };
  }

  function computeNextReferences(validFiles, isReplace) {
    const newRefs = validFiles.map(buildReferenceFromFile);
    let next = isReplace ? newRefs : [...(props.task.references || []), ...newRefs];
    return props.isAgentic ? dedupeReferencesByFilename(next) : next.slice(0, 1);
  }

  function tryApplyReferencesUpdate(nextReferences, newRefsCount, isReplace) {
    if (props.isAgentic) {
      const { fits, totalSizeBytes, memoryLimitBytes } = referencesFitMemoryLimit(nextReferences);
      if (!fits) {
        addToast(
          `Attached files total ${formatFileSize(totalSizeBytes)}, which exceeds container memory (${formatFileSize(memoryLimitBytes)}). Increase memory or remove files.`,
          'error'
        );
        return false;
      }
    }
    emit('updateTask', { ...props.task, references: nextReferences });
    const label = `${newRefsCount} file${newRefsCount > 1 ? 's' : ''} ${isReplace ? 'replaced' : 'attached'} successfully`;
    addToast(label, 'success');
    return true;
  }

  async function onFileChange(e) {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;

    const { files } = getFilesToProcess(selectedFiles);
    const { validFiles, errors } = await validateFilesAsync(files);

    errors.forEach(error => addToast(error, 'error'));

    const isReplace = pendingReferenceAction.value === 'replace';
    if (validFiles.length > 0) {
      const nextReferences = computeNextReferences(validFiles, isReplace);
      const newRefsCount = validFiles.length;
      const applied = tryApplyReferencesUpdate(nextReferences, newRefsCount, isReplace);
      if (!applied) {
        pendingReferenceAction.value = 'append';
        clearFileInput();
        return;
      }
    }

    pendingReferenceAction.value = 'append';
    clearFileInput();
  }
  
  function removeReference(refId) {
    if (props.isEditing || props.isExampleMode) return;
    const updatedRefs = (props.task.references || []).filter(ref => ref.id !== refId);
    emit('updateTask', { ...props.task, references: updatedRefs });
  }
  
  function clearReferences() {
    if (props.isEditing || props.isExampleMode) return;
    emit('updateTask', { ...props.task, references: [] });
  }

  function openFileDialog(action = 'append') {
    if (props.isEditing || props.isExampleMode) return;
    pendingReferenceAction.value = action;
    fileInputRef.value?.click();
  }
  
  function addCriterion() {
  if (props.isEditing || props.isExampleMode) return;

  const criteria = props.task.criteria || [];
  
  const newCriterion = {
    id: crypto.randomUUID(),
    name: '',
    graderType: 'llm-judge',
    description: '',
    weight: 10, 
    rationale: '',
    examples: [],
    semanticPrompt: ''
  };

  const listWithNewItem = [...criteria, newCriterion];

  const totalWeight = listWithNewItem.reduce((sum, c) => sum + (c.weight || 0), 0);

  if (totalWeight > 100) {
    emit('updateTask', { ...props.task, criteria: normalizeList(listWithNewItem) });
    addToast('Criterion added and weights normalized to 100%', 'info');
  } else {
    emit('updateTask', { ...props.task, criteria: listWithNewItem });
  }
}
  
function updateCriterion(index, updated) {
  if (props.isExampleMode) return;
  const criteria = normalizeCriteriaForTask([...(props.task.criteria || [])]);
  
  criteria[index] = normalizeCriterionForTask({ ...updated });
  
  emit('updateTask', { ...props.task, criteria });
}
  
  function deleteCriterion(index) {
    if (props.isExampleMode) return;
    const criteria = (props.task.criteria || []).filter((_, i) => i !== index);
    emit('updateTask', { ...props.task, criteria });
  }

function normalizeList(list) {
  if (list.length === 0) return [];

  const totalCurrentWeight = list.reduce((sum, c) => sum + (c.weight || 0), 0);
  let newWeights = [];

  if (totalCurrentWeight === 0) {
    const rawShare = 100 / list.length;
    const roundedShare = Math.round(rawShare * 100) / 100;
    newWeights = list.map(() => roundedShare);
  } else {
    newWeights = list.map(c => {
      const rawShare = ((c.weight || 0) / totalCurrentWeight) * 100;
      return Math.round(rawShare * 100) / 100;
    });
  }

  const currentSum = parseFloat(newWeights.reduce((sum, w) => sum + w, 0).toFixed(2));
  
  const diff = parseFloat((100 - currentSum).toFixed(2));

  if (diff !== 0) {
    const lastIndex = newWeights.length - 1;
    const newValue = newWeights[lastIndex] + diff;
    newWeights[lastIndex] = parseFloat(newValue.toFixed(2));
  }

  return list.map((c, i) => ({
    ...c,
    weight: Math.max(0.01, newWeights[i])
  }));
}
  
function normalizeCriteriaWeights() {
  if (props.isExampleMode) return;
  const criteria = props.task.criteria || [];
  if (criteria.length === 0) return;

  const normalized = normalizeList(criteria);
  emit('updateTask', { ...props.task, criteria: normalized });
  addToast('Weights normalized to integers summing 100%', 'success');
}
  
  function applyRubricPattern(pattern) {
    if (props.isExampleMode) return;
    emit('updateTask', { ...props.task, criteria: normalizeCriteriaForTask(pattern.criteria || []) });
    showRubricLibrary.value = false;
  }
  
  function updatePassThreshold(e) {
    if (props.isExampleMode) return;
    const value = parseFloat(e.target.value) || 70;
    const minValue = Math.max(10, value);
    emit('updateTask', { ...props.task, passThreshold: minValue });
  }

  function getEnvironment() {
    const defaults = defaultEnvironment();
    const env = props.task.environment || {};
    return {
      ...defaults,
      ...env,
      resources: { ...defaults.resources, ...(env.resources || {}) },
      timeouts: { ...defaults.timeouts, ...(env.timeouts || {}) },
    };
  }

  function emitTaskWithEnvironment(environment, options = {}) {
    const { markPresetCustomized = true } = options;
    const nextTask = { ...props.task, environment };
    const activeProfile = props.task.metadata?.environment_profile;

    if (markPresetCustomized && activeProfile && !activeProfile.customized) {
      nextTask.metadata = {
        ...defaultMetadata(),
        ...(props.task.metadata || {}),
        environment_profile: {
          ...activeProfile,
          customized: true,
        },
      };
    }

    emit('updateTask', nextTask);
  }

  function updateEnvironmentField(field, value) {
    if (props.isEditing || props.isExampleMode) return;
    const environment = getEnvironment();
    environment[field] = value;
    emitTaskWithEnvironment(environment);
  }

  function updateResourceField(field, value) {
    if (props.isEditing || props.isExampleMode) return;

    if (props.isAgentic && field === 'memory_mb') {
      const { fits, totalSizeBytes, memoryLimitBytes } = referencesFitMemoryLimit(props.task.references || [], value);
      if (!fits) {
        addToast(
          `Cannot set memory to ${formatFileSize(memoryLimitBytes)} while attached reference files total ${formatFileSize(totalSizeBytes)}.`,
          'error'
        );
        return;
      }
    }

    const environment = getEnvironment();
    environment.resources[field] = value;
    emitTaskWithEnvironment(environment);
  }

  function updateTimeoutField(field, value) {
    if (props.isEditing || props.isExampleMode) return;
    const environment = getEnvironment();
    environment.timeouts[field] = value;
    emitTaskWithEnvironment(environment);
  }

  function applyResourcePreset(key) {
    if (props.isEditing || props.isExampleMode) return;
    const preset = RESOURCE_PRESETS[key];
    if (!preset) return;

    if (props.isAgentic) {
      const { fits, totalSizeBytes, memoryLimitBytes } = referencesFitMemoryLimit(props.task.references || [], preset.memory_mb);
      if (!fits) {
        addToast(
          `Preset memory ${formatFileSize(memoryLimitBytes)} is below attached reference size (${formatFileSize(totalSizeBytes)}).`,
          'error'
        );
        return;
      }
    }

    const environment = getEnvironment();
    environment.resources = { ...preset };
    emitTaskWithEnvironment(environment);
  }

  function handleBaseImageChange(value) {
    if (value === 'custom') {
      updateEnvironmentField('base_image', '');
    } else {
      updateEnvironmentField('base_image', value);
    }
  }

  function clampInt(value, min, max) {
    const num = Number(value);
    if (!Number.isFinite(num)) return min;
    return Math.max(min, Math.min(max, Math.floor(num)));
  }

  function formatTimeoutHint(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) return `${mins} min`;
    return `${mins}m ${secs}s`;
  }

  function addSystemPackage() {
    if (props.isEditing || props.isExampleMode) return;
    const value = systemPackageInput.value.trim();
    if (!value) return;
    const current = props.task.environment?.system_packages || [];
    if (current.includes(value)) { systemPackageInput.value = ''; return; }
    updateEnvironmentField('system_packages', [...current, value]);
    systemPackageInput.value = '';
  }

  function addPythonPackage() {
    if (props.isEditing || props.isExampleMode) return;
    const value = pythonPackageInput.value.trim();
    if (!value) return;
    const current = props.task.environment?.python_packages || [];
    if (current.includes(value)) { pythonPackageInput.value = ''; return; }
    updateEnvironmentField('python_packages', [...current, value]);
    pythonPackageInput.value = '';
  }

  function removePackage(field, pkg) {
    if (props.isEditing || props.isExampleMode) return;
    const current = props.task.environment?.[field] || [];
    updateEnvironmentField(field, current.filter(p => p !== pkg));
  }

  function updateMetadataField(field, value) {
    if (props.isEditing || props.isExampleMode) return;
    const metadata = {
      ...defaultMetadata(),
      ...(props.task.metadata || {}),
    };
    metadata[field] = value;
    emit('updateTask', { ...props.task, metadata });
  }

  function addMetadataTag() {
    if (props.isEditing || props.isExampleMode) return;
    const value = tagInput.value.trim();
    if (!value) return;
    const current = props.task.metadata?.tags || [];
    if (current.includes(value)) { tagInput.value = ''; return; }
    updateMetadataField('tags', [...current, value]);
    tagInput.value = '';
  }

  function removeMetadataTag(tag) {
    if (props.isEditing || props.isExampleMode) return;
    const current = props.task.metadata?.tags || [];
    updateMetadataField('tags', current.filter(t => t !== tag));
  }

  async function loadAcceptedFormats() {
    if (props.isAgentic) {
      acceptedFormats.value = [];
      acceptedExtensions.value = '';
      return;
    }

    try {
      const response = await getAcceptedFileFormats();
      if (response.error) {
        console.error('Error loading accepted file formats:', response.message);
        return;
      }
      acceptedFormats.value = response.data || [];
      acceptedExtensions.value = acceptedFormats.value
        .map(format => `.${format.extension.toLowerCase()}`)
        .join(',');
    } catch (error) {
      console.error('Error loading accepted file formats:', error);
    }
  }
  
  onMounted(() => {
    loadAcceptedFormats();
  });

  watch(
    tabs,
    (nextTabs) => {
      if (!nextTabs.includes(activeTab.value)) {
        activeTab.value = nextTabs[0] || 'task';
      }
    },
    { immediate: true }
  );
  </script>
  
  <template>
    <div class="bg-echoes-background-black border border-echoes-border-gray rounded-xl p-3 w-full h-full flex flex-col overflow-hidden">
      <div class="flex justify-between items-center mb-4 pb-3 border-b border-echoes-border-gray/40 flex-shrink-0">
        <div class="flex gap-2">
          <button v-for="tab in tabs" :key="tab" :class="[
            'px-3 py-1.5 rounded-md text-xs 2xl:text-sm font-semibold cursor-pointer transition-all duration-200 capitalize',
            activeTab === tab
              ? 'bg-echoes-primary text-white shadow-sm'
              : 'bg-transparent text-white/80 hover:bg-white/5 hover:text-white/70'
          ]" @click="activeTab = tab" :data-testid="`tab-${tab}-button`">
            {{ getTabLabel(tab) }}
          </button>
      
        </div>
        <button
          v-if="canDelete && !props.isExampleMode"
          :disabled="props.isEditing"
          class="bg-red-500/20 text-red-400 border border-red-500/30 rounded-md py-1.5 px-3 text-xs 2xl:text-sm transition-all duration-200"
          :class="props.isEditing ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-red-500/30 hover:border-red-500/50'"
          @click="emit('deleteTask', task.id)"
          data-testid="delete-task-button">
          Delete Task
        </button>
      </div>

      <div v-if="activeTab === 'task'" class="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto">
        <div class="flex flex-col flex-1 min-h-[calc(100%_-_7rem)] gap-2">
          <label :for="`task-prompt`" class="text-sm font-semibold text-white/90 flex-shrink-0 flex items-center gap-2">
            Task <span class="text-red-500">*</span>
            <v-tooltip location="top" max-width="500">
              <template v-slot:activator="{ props }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary" v-bind="props">
                  <path fill="currentColor"
                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                  </path>
                </svg>
              </template>
              <span>Write the prompt/question the model will answer. If grading needs structure (partial credit, format rules, reasoning checks), define it in the Grading Rubric tab.</span>
            </v-tooltip>
          </label>
          <MarkdownRenderer 
            :model-value="task.prompt" 
            @update:model-value="emit('updateTask', { ...props.task, prompt: $event })"
            :disabled="props.isEditing || props.isExampleMode"
            placeholder="Write your task. Markdown and LaTeX supported."
            :content-only="false"
            :enable-image-upload="false" 
            dataTestId="task-prompt-textarea" />
        </div>
  
        <div class="w-full flex-shrink-0 rounded-xl border border-echoes-border-gray bg-echoes-background-black/60 p-3">
          <input
            ref="fileInputRef"
            type="file"
            :multiple="props.isAgentic"
            :accept="referenceInputAccept"
            @change="onFileChange"
            class="hidden"
            data-testid="reference-file-input"
          />

          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <span class="text-sm font-semibold text-white/90 block truncate">
                {{ referenceTitle }}
                <span class="text-white/50 font-normal">({{ referencesCount }})</span>
              </span>
              <p class="text-[11px] text-white/50 mt-1 mb-0 line-clamp-2">{{ referenceHelpText }}</p>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <button
                @click="openFileDialog(hasReferences ? 'replace' : 'append')"
                :disabled="props.isEditing || props.isExampleMode"
                :class="[
                  'py-2 px-3 rounded-lg text-xs font-medium text-white transition-all duration-200 flex items-center gap-2',
                  props.isEditing || props.isExampleMode
                    ? 'bg-echoes-border-light/50 cursor-not-allowed'
                    : 'bg-echoes-border-light hover:bg-echoes-border-light/80 cursor-pointer'
                ]"
                data-testid="attach-reference-file-button"
              >
                <i class="uil uil-paperclip text-sm"></i>
                <span>{{ referenceActionLabel }}</span>
              </button>

              <button
                v-if="props.isAgentic && hasReferences"
                @click="openFileDialog('append')"
                :disabled="props.isEditing || props.isExampleMode"
                :class="[
                  'py-2 px-3 rounded-lg text-xs font-medium border border-echoes-border-gray text-white/80 transition-all duration-200',
                  props.isEditing || props.isExampleMode
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:text-white hover:border-white/30'
                ]"
              >
                Add More
              </button>

              <button
                v-if="hasReferences"
                @click="clearReferences"
                :disabled="props.isEditing || props.isExampleMode"
                :class="[
                  'py-2 px-3 rounded-lg text-xs font-medium border border-red-500/40 text-red-400 transition-all duration-200',
                  props.isEditing || props.isExampleMode
                    ? 'cursor-not-allowed opacity-50'
                    : 'cursor-pointer hover:bg-red-500/10 hover:border-red-500/60'
                ]"
              >
                Remove All
              </button>
            </div>
          </div>

          <div
            v-if="hasReferences"
            class="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-2"
          >
            <div
              v-for="ref in task.references"
              :key="ref.id"
              class="bg-echoes-background-black border border-echoes-border-gray rounded-lg p-3 flex items-center justify-between gap-3 group hover:border-echoes-primary/50 transition-all duration-200"
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div class="flex-shrink-0 w-8 h-8 bg-echoes-primary/20 border border-echoes-primary/40 rounded-lg flex items-center justify-center">
                  <i :class="[isImageReference(ref) ? 'uil uil-image' : 'uil uil-file', 'text-echoes-primary text-lg']"></i>
                </div>
                <div class="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span class="text-white text-sm font-medium truncate">{{ ref.filename }}</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white/50 text-xs">{{ formatReferenceType(ref) }}</span>
                    <span v-if="ref.size" class="text-white/40 text-xs">{{ formatFileSize(ref.size) }}</span>
                  </div>
                </div>
              </div>
              <button
                @click="removeReference(ref.id)"
                :disabled="props.isEditing || props.isExampleMode"
                :class="[
                  'flex-shrink-0 w-8 h-8 rounded-lg border text-red-400 transition-all duration-200 flex items-center justify-center',
                  props.isEditing || props.isExampleMode
                    ? 'bg-red-500/5 border-red-500/15 cursor-not-allowed'
                    : 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50 cursor-pointer'
                ]"
                title="Remove file"
                data-testid="remove-reference-file-button"
              >
                <i class="uil uil-times text-base"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
  
      <div v-if="activeTab === 'answer'" class="flex flex-col gap-2 flex-1 min-h-0">
        <label :for="`answer-answer`" class="text-sm font-semibold text-white flex-shrink-0 flex items-center gap-2">
          {{ answerTabTitle }} <span class="text-red-400">*</span>
          <v-tooltip location="top" max-width="500">
            <template v-slot:activator="{ props }">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary" v-bind="props">
                <path fill="currentColor"
                  d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                </path>
              </svg>
            </template>
            <span>{{ answerTooltipText }}</span>
          </v-tooltip>
        </label>
        <p class="text-white/60 text-xs m-0">{{ answerDescriptionText }}</p>
        <div class="flex-1 min-h-0">
          <MarkdownRenderer 
            :model-value="task.answer"
            @update:model-value="emit('updateTask', { ...props.task, answer: $event })"
            :disabled="props.isEditing || props.isExampleMode"
            placeholder="Write your answer. Markdown and LaTeX supported."
            :content-only="false"
            :enable-image-upload="false"
            dataTestId="task-answer-textarea" />
        </div>
      </div>
  
      <div v-if="activeTab === 'criterion'" class="flex flex-col gap-6 overflow-auto flex-1 min-h-0">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h3 class="text-sm font-semibold text-white flex items-center gap-2">
              How should this task be graded?
              <v-tooltip location="top" max-width="500">
                <template v-slot:activator="{ props }">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary" v-bind="props">
                    <path fill="currentColor"
                      d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                    </path>
                  </svg>
                </template>
                <span>{{ criteriaTooltipText }}</span>
              </v-tooltip>
            </h3>
            <p class="text-white/60 text-xs m-0">{{ criteriaDescriptionText }}</p>
          </div>
        </div>
  
        <div v-if="(task.criteria || []).length === 0"
          class="bg-transparent border-2 border-dashed border-echoes-border-gray rounded-lg p-8 text-center">
          <div class="flex flex-col items-center gap-1">
            <i class="uil uil-clipboard-notes text-4xl text-white/50"></i>
            <p class="text-white/80 text-sm font-medium">No grading rubric defined yet.</p>
            <p class="text-white/50 text-xs m-0">Start by adding at least one grading criterion below.</p>
          </div>
        </div>
  
        <div v-else class="flex flex-col gap-4">
          <CriterionCard v-for="(criterion, idx) in (task.criteria || [])" :key="criterion.id" :criterion="criterion"
            :index="idx" :allCriteria="task.criteria || []" :isReadOnly="props.isExampleMode" :isAgentic="props.isAgentic"
            @update="updateCriterion(idx, $event)" @delete="deleteCriterion(idx)" />
        </div>
  
        <button
          :disabled="props.isEditing || props.isExampleMode"
          :class="[
            'w-full border border-dashed rounded-lg py-3 px-4 text-white font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2',
            props.isEditing || props.isExampleMode
              ? 'bg-echoes-primary/5 border-echoes-primary/25 cursor-not-allowed' 
              : 'bg-echoes-primary/10 border-echoes-primary/50 cursor-pointer hover:bg-echoes-primary/15 hover:border-solid'
          ]"
          @click="addCriterion"
          data-testid="add-criterion-button">
          <i class="uil uil-plus text-base"></i>
          <span>Add Grading Criterion</span>
        </button>
  
        <WeightingBar v-if="(task.criteria || []).length > 0" :criteria="task.criteria"
          @normalize="normalizeCriteriaWeights" />
  
        <div v-if="(task.criteria || []).length > 0"
          class="bg-echoes-background-black border border-echoes-border-gray rounded-xl p-4">
          <label :for="`pass-threshold`" class="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            Pass Threshold
            <v-tooltip location="top" max-width="300">
              <template v-slot:activator="{ props }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary" v-bind="props">
                  <path fill="currentColor"
                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                  </path>
                </svg>
              </template>
              <span>Minimum overall score required to pass (e.g., 70% means a model must meet at least 7 criteria in a task with 10 criteria)</span>
            </v-tooltip>
          </label>
          <p class="text-white/70 text-xs mb-4">
            Consider this task passed if overall score is ≥ this percentage
          </p>
          <div class="flex items-center gap-4">
            <input type="range" :disabled="props.isEditing || props.isExampleMode"
              :class="[
                'flex-1 h-2 rounded-full bg-echoes-border-gray/50 border border-echoes-border-gray/50 appearance-none',
                props.isEditing || props.isExampleMode
                  ? 'cursor-not-allowed' 
                  : 'cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-echoes-primary [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-white [&::-moz-range-thumb]:bg-echoes-primary [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:border-none [&::-moz-range-track]:bg-echoes-border-gray/50'
              ]"
              :style="{
                background: `linear-gradient(to right, rgb(var(--echoes-primary)) 0%, rgb(var(--echoes-primary)) ${Math.max(10, task.passThreshold || 70)}%, rgb(var(--echoes-border-gray) / 0.5) ${Math.max(10, task.passThreshold || 70)}%, rgb(var(--echoes-border-gray) / 0.5) 100%)`
              }"
              :value="Math.max(10, task.passThreshold || 70)" @input="updatePassThreshold" min="10" max="100" step="5"
              data-testid="pass-threshold-slider" />
            <span class="text-xl font-bold text-echoes-primary min-w-[60px] text-right">
              {{ Math.max(10, task.passThreshold || 70) }}%
            </span>
          </div>
        </div>
      </div>

      <div v-if="activeTab === 'environment'" class="flex-1 min-h-0 overflow-y-auto">
        <div class="pr-1 pb-4 flex flex-col gap-5">

        <!-- Task Metadata -->
        <div class="rounded-xl border border-echoes-border-gray bg-echoes-background-black/70 p-4">
          <div class="mb-4">
            <h3 class="text-sm font-semibold uppercase tracking-wide text-white/70">Task Metadata</h3>
            <p class="text-xs text-white/50 mt-0.5 m-0">Categorize this task for benchmark organization.</p>
          </div>

          <div class="grid grid-cols-3 gap-4">
            <fieldset class="flex flex-col gap-1.5 border-0 p-0 m-0 min-w-0">
              <legend class="text-xs font-medium text-white/80">Difficulty</legend>
              <div class="flex gap-1.5">
                <button
                  v-for="level in ['easy', 'medium', 'hard']" :key="level"
                  :disabled="props.isEditing || props.isExampleMode"
                  @click="updateMetadataField('difficulty', level)"
                  :class="[
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize',
                    (task.metadata?.difficulty || 'medium') === level
                      ? 'bg-echoes-primary/20 border border-echoes-primary text-echoes-primary'
                      : 'bg-echoes-background-gray border border-echoes-border-gray text-white/50 hover:text-white/70',
                    (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed' : 'cursor-pointer'
                  ]"
                  :data-testid="`metadata-difficulty-${level}`">
                  {{ level }}
                </button>
              </div>
            </fieldset>

            <div class="flex flex-col gap-1.5 col-span-2">
              <label for="metadata-category" class="text-xs font-medium text-white/80">Category</label>
              <input
                id="metadata-category"
                type="text"
                :disabled="props.isEditing || props.isExampleMode"
                :value="task.metadata?.category || ''"
                @input="updateMetadataField('category', $event.target.value)"
                placeholder="e.g., software-engineering, mathematics"
                class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                data-testid="metadata-category"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1.5 mt-4">
            <label for="metadata-tag-input" class="text-xs font-medium text-white/80">Tags</label>
            <div class="flex flex-wrap gap-1.5 mb-1" v-if="(task.metadata?.tags || []).length">
              <span
                v-for="tag in task.metadata?.tags" :key="tag"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-xs text-white/80">
                {{ tag }}
                <button
                  v-if="!(props.isEditing || props.isExampleMode)"
                  @click="removeMetadataTag(tag)"
                  class="hover:text-white cursor-pointer ml-0.5">
                  <i class="uil uil-times text-[10px]"></i>
                </button>
              </span>
            </div>
            <div class="flex gap-2">
              <input
                id="metadata-tag-input"
                type="text"
                v-model="tagInput"
                :disabled="props.isEditing || props.isExampleMode"
                @keydown.enter.prevent="addMetadataTag"
                placeholder="Type a tag and press Enter"
                class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                data-testid="metadata-tag-input"
              />
              <button
                :disabled="props.isEditing || props.isExampleMode"
                @click="addMetadataTag"
                :class="[
                  'px-3 py-1.5 rounded-lg bg-echoes-primary/15 border border-echoes-primary/40 text-echoes-primary text-xs font-medium transition-all',
                  (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-echoes-primary/25'
                ]">
                Add
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4 mt-4">
            <div class="flex flex-col gap-1.5">
              <label for="metadata-expert-time" class="text-xs font-medium text-white/80">Expert Time Estimate</label>
              <div class="flex items-center gap-2">
                <input
                  id="metadata-expert-time"
                  type="number" min="1"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.metadata?.expert_time_estimate_min || ''"
                  @input="updateMetadataField('expert_time_estimate_min', $event.target.value ? Number($event.target.value) : null)"
                  placeholder="—"
                  class="w-20 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="metadata-expert-time"
                />
                <span class="text-xs text-white/40">min</span>
              </div>
            </div>
            <div class="flex flex-col gap-1.5">
              <label for="metadata-junior-time" class="text-xs font-medium text-white/80">Junior Time Estimate</label>
              <div class="flex items-center gap-2">
                <input
                  id="metadata-junior-time"
                  type="number" min="1"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.metadata?.junior_time_estimate_min || ''"
                  @input="updateMetadataField('junior_time_estimate_min', $event.target.value ? Number($event.target.value) : null)"
                  placeholder="—"
                  class="w-20 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="metadata-junior-time"
                />
                <span class="text-xs text-white/40">min</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Presets -->
        <div class="rounded-xl border border-echoes-border-gray bg-echoes-background-black/70 overflow-hidden">
          <button
            class="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-all"
            @click="toggleEnvironmentSection('presets')"
            data-testid="environment-presets-toggle">
            <div class="text-left">
              <h3 class="text-sm font-semibold uppercase tracking-wide text-white/70">Presets</h3>
              <p class="text-xs text-white/50 mt-0.5 m-0">Start from a domain bundle, then customize below.</p>
            </div>
            <i :class="[
              'uil text-sm text-white/50 transition-transform duration-200',
              isEnvironmentSectionOpen('presets') ? 'uil-angle-down' : 'uil-angle-right'
            ]"></i>
          </button>

          <div v-show="isEnvironmentSectionOpen('presets')" class="px-4 pb-4">
            <div class="flex flex-wrap items-center gap-2 mb-4">
              <button
                v-for="domain in environmentPresetDomains" :key="domain"
                :disabled="props.isEditing || props.isExampleMode"
                @click="environmentPresetDomainFilter = domain"
                :class="[
                  'px-3 py-1 rounded-full text-xs border transition-all',
                  environmentPresetDomainFilter === domain
                    ? 'bg-echoes-primary/20 border-echoes-primary text-echoes-primary'
                    : 'bg-echoes-background-gray border-echoes-border-gray text-white/60 hover:text-white/80 hover:border-white/20',
                  (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                ]">
                {{ formatEnvironmentPresetDomain(domain) }}
              </button>

              <button
                v-if="activeEnvironmentProfile"
                :disabled="props.isEditing || props.isExampleMode"
                @click="clearEnvironmentPresetProfile"
                :class="[
                  'ml-auto px-3 py-1 rounded-full text-xs border transition-all',
                  'bg-transparent border-echoes-border-gray text-white/50 hover:text-white/80 hover:border-white/20',
                  (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                ]">
                Clear Preset Label
              </button>
            </div>

            <div v-if="activeEnvironmentProfile" class="rounded-lg border border-echoes-primary/25 bg-echoes-primary/5 px-3 py-2 mb-4">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <div class="text-sm text-white font-medium">
                    {{ activeEnvironmentProfile.preset_name || activeEnvironmentProfile.preset_id }}
                    <span class="text-white/40 font-normal ml-1">{{ activeEnvironmentProfile.env_version }}</span>
                  </div>
                  <p class="text-[11px] text-white/50 m-0 mt-1">
                    {{ formatEnvironmentPresetDomain(activeEnvironmentProfile.domain) }}
                    · {{ activeEnvironmentProfile.container_mode === 'dockerfile' ? 'Dockerfile preset' : 'Simple preset' }}
                    · {{ activeEnvironmentProfile.network_policy || 'offline' }}
                  </p>
                  <p class="text-[11px] text-white/40 m-0 mt-1">
                    Tooling: {{ formatEnvironmentPresetList(activeEnvironmentProfile.tooling) }}
                  </p>
                </div>
                <span :class="[
                  'px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide whitespace-nowrap',
                  activeEnvironmentProfile.customized
                    ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
                    : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
                ]">
                  {{ activeEnvironmentProfile.customized ? 'Customized' : 'Unmodified' }}
                </span>
              </div>
            </div>

            <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
              <div
                v-for="preset in filteredEnvironmentPresets" :key="preset.id"
                :class="[
                  'rounded-lg border p-3',
                  isEnvironmentPresetActive(preset.id)
                    ? 'border-echoes-primary/50 bg-echoes-primary/5'
                    : 'border-echoes-border-gray bg-echoes-background-black'
                ]"
                :data-testid="`environment-preset-${preset.id}`">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <div class="text-sm font-medium text-white">{{ preset.name }}</div>
                    <p class="text-[11px] text-white/50 m-0 mt-0.5">
                      {{ formatEnvironmentPresetDomain(preset.domain) }} · {{ preset.env_version }}
                    </p>
                  </div>
                  <span class="px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-[10px] text-white/70 uppercase tracking-wide">
                    {{ preset.mode === 'dockerfile' ? 'Dockerfile' : 'Simple' }}
                  </span>
                </div>

                <p class="text-xs text-white/70 m-0 mt-2">{{ preset.description }}</p>

                <div class="mt-3 grid grid-cols-1 gap-1.5 text-[11px]">
                  <div class="text-white/50">
                    <span class="text-white/70">Modality:</span> {{ formatEnvironmentPresetList(preset.data_modality) }}
                  </div>
                  <div class="text-white/50">
                    <span class="text-white/70">Tooling:</span> {{ formatEnvironmentPresetList(preset.tooling) }}
                  </div>
                  <div class="text-white/50">
                    <span class="text-white/70">Network:</span> {{ preset.network_policy || 'offline' }}
                  </div>
                  <div class="text-white/50">
                    <span class="text-white/70">Resources:</span>
                    {{ preset.environment?.resources?.cpus ?? 1 }} CPU ·
                    {{ Math.round((preset.environment?.resources?.memory_mb ?? 2048) / 1024) }} GB
                  </div>
                </div>

                <div class="mt-3 flex items-center justify-between gap-2">
                  <span class="text-[10px] text-white/35">
                    {{ preset.source_repo }} @ {{ preset.source_ref }}
                  </span>
                  <button
                    :disabled="props.isEditing || props.isExampleMode"
                    @click="applyEnvironmentPreset(preset.id)"
                    :class="[
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                      isEnvironmentPresetActive(preset.id)
                        ? 'bg-echoes-primary/20 border-echoes-primary text-echoes-primary'
                        : 'bg-echoes-background-gray border-echoes-border-gray text-white/80 hover:border-white/20 hover:text-white',
                      (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    ]">
                    {{ isEnvironmentPresetActive(preset.id) ? 'Reapply Preset' : 'Apply Preset' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Container -->
        <div class="rounded-xl border border-echoes-border-gray bg-echoes-background-black/70 overflow-hidden">
          <button
            class="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-all"
            @click="toggleEnvironmentSection('container')"
            data-testid="environment-container-toggle">
            <div class="text-left">
              <h3 class="text-sm font-semibold uppercase tracking-wide text-white/70">Container</h3>
              <p class="text-xs text-white/50 mt-0.5 m-0">Docker image and packages for the runtime.</p>
            </div>
            <i :class="[
              'uil text-sm text-white/50 transition-transform duration-200',
              isEnvironmentSectionOpen('container') ? 'uil-angle-down' : 'uil-angle-right'
            ]"></i>
          </button>

          <div v-show="isEnvironmentSectionOpen('container')" class="px-4 pb-4">
            <div class="flex justify-end mb-4">
              <div class="flex rounded-lg border border-echoes-border-gray overflow-hidden flex-shrink-0">
                <button
                  @click="switchContainerMode('simple')"
                  :disabled="props.isEditing || props.isExampleMode"
                  :class="[
                    'px-3 py-1 text-[11px] font-medium transition-all',
                    containerMode === 'simple'
                      ? 'bg-echoes-primary/20 text-echoes-primary'
                      : 'bg-transparent text-white/40 hover:text-white/60',
                    (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed' : 'cursor-pointer'
                  ]"
                  data-testid="container-mode-simple">
                  Simple
                </button>
                <button
                  @click="switchContainerMode('dockerfile')"
                  :disabled="props.isEditing || props.isExampleMode"
                  :class="[
                    'px-3 py-1 text-[11px] font-medium transition-all border-l border-echoes-border-gray',
                    containerMode === 'dockerfile'
                      ? 'bg-echoes-primary/20 text-echoes-primary'
                      : 'bg-transparent text-white/40 hover:text-white/60',
                    (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed' : 'cursor-pointer'
                  ]"
                  data-testid="container-mode-dockerfile">
                  Dockerfile
                </button>
              </div>
            </div>

            <!-- Simple mode -->
            <template v-if="containerMode === 'simple'">
              <div class="flex flex-col gap-1.5">
                <label for="environment-base-image" class="text-xs font-medium text-white/80">Base Image</label>
                <select
                  id="environment-base-image"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="isCustomBaseImage ? 'custom' : (task.environment?.base_image || commonBaseImages[0])"
                  @change="handleBaseImageChange($event.target.value)"
                  class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="environment-base-image">
                  <option v-for="img in commonBaseImages" :key="img" :value="img">{{ img }}</option>
                  <option value="custom">Custom...</option>
                </select>
                <input
                  v-if="isCustomBaseImage"
                  type="text"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.environment?.base_image || ''"
                  @input="updateEnvironmentField('base_image', $event.target.value)"
                  placeholder="e.g., nvidia/cuda:12.0-devel-ubuntu22.04"
                  class="mt-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="environment-base-image-custom"
                />
              </div>

              <div class="flex flex-col gap-1.5 mt-4">
                <label for="environment-system-packages" class="text-xs font-medium text-white/80">System Packages</label>
                <p class="text-[11px] text-white/40 m-0">apt-get packages installed in the container.</p>
                <div class="flex flex-wrap gap-1.5 mb-1" v-if="(task.environment?.system_packages || []).length">
                  <span
                    v-for="pkg in task.environment?.system_packages" :key="pkg"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-xs text-white/80 font-mono">
                    {{ pkg }}
                    <button
                      v-if="!(props.isEditing || props.isExampleMode)"
                      @click="removePackage('system_packages', pkg)"
                      class="hover:text-white cursor-pointer ml-0.5">
                      <i class="uil uil-times text-[10px]"></i>
                    </button>
                  </span>
                </div>
                <div class="flex gap-2">
                  <input
                    id="environment-system-packages"
                    type="text"
                    v-model="systemPackageInput"
                    :disabled="props.isEditing || props.isExampleMode"
                    @keydown.enter.prevent="addSystemPackage"
                    placeholder="e.g., git, build-essential"
                    class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white font-mono placeholder:text-white/30 placeholder:font-sans focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                    data-testid="environment-system-packages"
                  />
                  <button
                    :disabled="props.isEditing || props.isExampleMode"
                    @click="addSystemPackage"
                    :class="[
                      'px-3 py-1.5 rounded-lg bg-echoes-primary/15 border border-echoes-primary/40 text-echoes-primary text-xs font-medium transition-all',
                      (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-echoes-primary/25'
                    ]">
                    Add
                  </button>
                </div>
              </div>

              <div class="flex flex-col gap-1.5 mt-4">
                <label for="environment-python-packages" class="text-xs font-medium text-white/80">Python Packages</label>
                <p class="text-[11px] text-white/40 m-0">pip packages with optional version pinning.</p>
                <div class="flex flex-wrap gap-1.5 mb-1" v-if="(task.environment?.python_packages || []).length">
                  <span
                    v-for="pkg in task.environment?.python_packages" :key="pkg"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-xs text-white/80 font-mono">
                    {{ pkg }}
                    <button
                      v-if="!(props.isEditing || props.isExampleMode)"
                      @click="removePackage('python_packages', pkg)"
                      class="hover:text-white cursor-pointer ml-0.5">
                      <i class="uil uil-times text-[10px]"></i>
                    </button>
                  </span>
                </div>
                <div class="flex gap-2">
                  <input
                    id="environment-python-packages"
                    type="text"
                    v-model="pythonPackageInput"
                    :disabled="props.isEditing || props.isExampleMode"
                    @keydown.enter.prevent="addPythonPackage"
                    placeholder="e.g., torch==2.7.1, numpy"
                    class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white font-mono placeholder:text-white/30 placeholder:font-sans focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                    data-testid="environment-python-packages"
                  />
                  <button
                    :disabled="props.isEditing || props.isExampleMode"
                    @click="addPythonPackage"
                    :class="[
                      'px-3 py-1.5 rounded-lg bg-echoes-primary/15 border border-echoes-primary/40 text-echoes-primary text-xs font-medium transition-all',
                      (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-echoes-primary/25'
                    ]">
                    Add
                  </button>
                </div>
              </div>
            </template>

            <!-- Dockerfile mode -->
            <template v-else>
              <div class="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 mb-3">
                <p class="text-[11px] text-amber-400/90 m-0">
                  Custom Dockerfiles are subject to review. Do not include secrets, credentials, or API keys.
                </p>
              </div>

              <textarea
                :disabled="props.isEditing || props.isExampleMode"
                :value="task.environment?.dockerfile || ''"
                @input="updateEnvironmentField('dockerfile', $event.target.value)"
                placeholder="FROM ubuntu:24.04&#10;WORKDIR /app&#10;RUN apt-get update && apt-get install -y git python3-pip&#10;COPY make.py /app/&#10;RUN python3 make.py"
                rows="14"
                :class="[
                  'w-full rounded-lg border bg-[#0d1117] px-3 py-2 text-sm text-green-300 font-mono leading-relaxed placeholder:text-white/20 focus:outline-none resize-y disabled:cursor-not-allowed',
                  hasDockerfileErrors ? 'border-red-500/50' : 'border-echoes-border-gray focus:border-echoes-primary'
                ]"
                spellcheck="false"
                data-testid="environment-dockerfile"
              ></textarea>

              <div class="flex items-center justify-between mt-1.5">
                <div class="flex flex-col gap-1">
                  <div v-for="(warning, idx) in dockerfileWarnings" :key="idx"
                    :class="[
                      'text-[11px] flex items-center gap-1',
                      warning.level === 'error' ? 'text-red-400' : 'text-amber-400'
                    ]">
                    <i :class="warning.level === 'error' ? 'uil uil-exclamation-triangle' : 'uil uil-info-circle'" class="text-xs"></i>
                    {{ warning.message }}
                  </div>
                </div>
                <span class="text-[10px] text-white/20 flex-shrink-0">
                  {{ (task.environment?.dockerfile || '').length.toLocaleString() }} / {{ DOCKERFILE_MAX_LENGTH.toLocaleString() }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- Resources -->
        <div class="rounded-xl border border-echoes-border-gray bg-echoes-background-black/70 overflow-hidden">
          <button
            class="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-all"
            @click="toggleEnvironmentSection('resources')"
            data-testid="environment-resources-toggle">
            <div class="text-left">
              <h3 class="text-sm font-semibold uppercase tracking-wide text-white/70">Resources</h3>
              <p class="text-xs text-white/50 mt-0.5 m-0">Compute limits for this task's container.</p>
            </div>
            <i :class="[
              'uil text-sm text-white/50 transition-transform duration-200',
              isEnvironmentSectionOpen('resources') ? 'uil-angle-down' : 'uil-angle-right'
            ]"></i>
          </button>

          <div v-show="isEnvironmentSectionOpen('resources')" class="px-4 pb-4">
            <div class="flex gap-2 mb-4">
              <button
                v-for="(preset, key) in RESOURCE_PRESETS" :key="key"
                :disabled="props.isEditing || props.isExampleMode"
                @click="applyResourcePreset(key)"
                :class="[
                  'px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize border',
                  activeResourcePreset === key
                    ? 'bg-echoes-primary/20 border-echoes-primary text-echoes-primary'
                    : 'bg-echoes-background-gray border-echoes-border-gray text-white/50 hover:text-white/70 hover:border-white/20',
                  (props.isEditing || props.isExampleMode) ? 'cursor-not-allowed' : 'cursor-pointer'
                ]"
                :data-testid="`resource-preset-${key}`">
                {{ key }}
                <span class="text-white/30 ml-1 text-[10px]">{{ preset.cpus }} CPU · {{ preset.memory_mb / 1024 }}GB</span>
              </button>
            </div>

            <div class="grid grid-cols-2 gap-x-4 gap-y-3">
              <div class="flex flex-col gap-1">
                <label for="resource-cpus" class="text-xs font-medium text-white/70">CPUs</label>
                <input
                  id="resource-cpus"
                  type="number" min="1" max="8"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.environment?.resources?.cpus ?? 1"
                  @input="updateResourceField('cpus', clampInt($event.target.value, 1, 8))"
                  class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="resource-cpus"
                />
                <p class="text-[10px] text-white/30 m-0">1 – 8 cores</p>
              </div>

              <div class="flex flex-col gap-1">
                <label for="resource-memory" class="text-xs font-medium text-white/70">Memory</label>
                <select
                  id="resource-memory"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.environment?.resources?.memory_mb ?? 2048"
                  @change="updateResourceField('memory_mb', Number($event.target.value))"
                  class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="resource-memory">
                  <option :value="2048">2 GB</option>
                  <option :value="4096">4 GB</option>
                  <option :value="8192">8 GB</option>
                  <option :value="16384">16 GB</option>
                </select>
                <p class="text-[10px] text-white/30 m-0">2 GB – 16 GB</p>
              </div>

              <div class="flex flex-col gap-1">
                <label for="resource-storage" class="text-xs font-medium text-white/70">Storage</label>
                <select
                  id="resource-storage"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.environment?.resources?.storage_mb ?? 10240"
                  @change="updateResourceField('storage_mb', Number($event.target.value))"
                  class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="resource-storage">
                  <option :value="1024">1 GB</option>
                  <option :value="2048">2 GB</option>
                  <option :value="5120">5 GB</option>
                  <option :value="10240">10 GB</option>
                  <option :value="20480">20 GB</option>
                  <option :value="51200">50 GB</option>
                </select>
                <p class="text-[10px] text-white/30 m-0">1 GB – 50 GB</p>
              </div>

              <div class="flex flex-col gap-1">
                <label for="resource-gpus" class="text-xs font-medium text-white/70">GPUs</label>
                <input
                  id="resource-gpus"
                  type="number" min="0" max="4"
                  :disabled="props.isEditing || props.isExampleMode"
                  :value="task.environment?.resources?.gpus ?? 0"
                  @input="updateResourceField('gpus', clampInt($event.target.value, 0, 4))"
                  class="rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                  data-testid="resource-gpus"
                />
                <p class="text-[10px] text-white/30 m-0">0 – 4 GPUs</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeouts -->
        <div class="rounded-xl border border-echoes-border-gray bg-echoes-background-black/70 overflow-hidden">
          <button
            class="w-full flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/5 transition-all"
            @click="toggleEnvironmentSection('timeouts')"
            data-testid="environment-timeouts-toggle">
            <div class="text-left">
              <h3 class="text-sm font-semibold uppercase tracking-wide text-white/70">Timeouts</h3>
              <p class="text-xs text-white/50 mt-0.5 m-0">Maximum time allowed for each phase, in seconds.</p>
            </div>
            <i :class="[
              'uil text-sm text-white/50 transition-transform duration-200',
              isEnvironmentSectionOpen('timeouts') ? 'uil-angle-down' : 'uil-angle-right'
            ]"></i>
          </button>

          <div v-show="isEnvironmentSectionOpen('timeouts')" class="px-4 pb-4">
            <div class="grid grid-cols-3 gap-4">
              <div class="flex flex-col gap-1">
                <label for="timeout-agent" class="text-xs font-medium text-white/70">Agent</label>
                <div class="flex items-center gap-2">
                  <input
                    id="timeout-agent"
                    type="number" min="300" max="14400" step="60"
                    :disabled="props.isEditing || props.isExampleMode"
                    :value="task.environment?.timeouts?.agent_sec ?? 900"
                    @input="updateTimeoutField('agent_sec', clampInt($event.target.value, 300, 14400))"
                    class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                    data-testid="timeout-agent"
                  />
                  <span class="text-xs text-white/40">sec</span>
                </div>
                <p class="text-[10px] text-white/30 m-0">300 – 14400s · {{ formatTimeoutHint(task.environment?.timeouts?.agent_sec ?? 900) }}</p>
              </div>

              <div class="flex flex-col gap-1">
                <label for="timeout-verifier" class="text-xs font-medium text-white/70">Verifier</label>
                <div class="flex items-center gap-2">
                  <input
                    id="timeout-verifier"
                    type="number" min="300" max="14400" step="60"
                    :disabled="props.isEditing || props.isExampleMode"
                    :value="task.environment?.timeouts?.verifier_sec ?? 900"
                    @input="updateTimeoutField('verifier_sec', clampInt($event.target.value, 300, 14400))"
                    class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                    data-testid="timeout-verifier"
                  />
                  <span class="text-xs text-white/40">sec</span>
                </div>
                <p class="text-[10px] text-white/30 m-0">300 – 14400s · {{ formatTimeoutHint(task.environment?.timeouts?.verifier_sec ?? 900) }}</p>
              </div>

              <div class="flex flex-col gap-1">
                <label for="timeout-build" class="text-xs font-medium text-white/70">Build</label>
                <div class="flex items-center gap-2">
                  <input
                    id="timeout-build"
                    type="number" min="300" max="1800" step="60"
                    :disabled="props.isEditing || props.isExampleMode"
                    :value="task.environment?.timeouts?.build_sec ?? 600"
                    @input="updateTimeoutField('build_sec', clampInt($event.target.value, 300, 1800))"
                    class="flex-1 rounded-lg border border-echoes-border-gray bg-echoes-background-black px-3 py-1.5 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
                    data-testid="timeout-build"
                  />
                  <span class="text-xs text-white/40">sec</span>
                </div>
                <p class="text-[10px] text-white/30 m-0">300 – 1800s · {{ formatTimeoutHint(task.environment?.timeouts?.build_sec ?? 600) }}</p>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>
  
      <RubricPatternLibrary v-if="showRubricLibrary" :isOpen="showRubricLibrary" @applyPattern="applyRubricPattern"
        @close="showRubricLibrary = false" />
  
      <div v-if="activeTab === 'preview'"
        class="flex-1 min-h-0 overflow-y-auto">
        <div class="flex flex-col gap-4">
        
        <!-- Task Section -->
        <div class="border border-echoes-border-gray rounded-xl overflow-hidden">
          <button 
            @click="previewTaskExpanded = !previewTaskExpanded"
            class="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-all duration-200 cursor-pointer">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary flex-shrink-0">
              <i class="mdi mdi-clipboard-text-outline text-echoes-primary text-lg"></i>
            </div>
            <div class="flex-1 text-left">
              <h2 class="text-sm uppercase text-echoes-primary font-semibold tracking-wider">TASK</h2>
              <p class="text-xs text-white/50 mt-0.5">{{ previewTaskExpanded ? 'Click to collapse' : 'Click to expand' }}</p>
            </div>
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary/50 bg-echoes-primary/10 flex-shrink-0">
              <i :class="[
                'mdi text-echoes-primary transition-transform duration-200',
                previewTaskExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              ]"></i>
            </div>
          </button>
          <div v-if="previewTaskExpanded" class="p-4 m-4 bg-echoes-background-gray/50 rounded-lg">
            <MarkdownRenderer :model-value="task.prompt" :is-editing="false" :content-only="true" />
          </div>
        </div>

        <!-- Reference Files Section -->
        <div v-if="(task.references || []).length > 0" class="border border-echoes-border-gray rounded-xl">
          <button 
            @click="previewReferencesExpanded = !previewReferencesExpanded"
            class="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-all duration-200 cursor-pointer">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary flex-shrink-0">
              <i class="mdi mdi-file-multiple-outline text-echoes-primary text-lg"></i>
            </div>
            <div class="flex-1 text-left">
              <h2 class="text-sm uppercase text-echoes-primary font-semibold tracking-wider">
                REFERENCE FILES
                <span class="text-white/60 normal-case font-medium">({{ task.references.length }})</span>
              </h2>
              <p class="text-xs text-white/50 mt-0.5">{{ previewReferencesExpanded ? 'Click to collapse' : 'Click to expand' }}</p>
            </div>
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary/50 bg-echoes-primary/10 flex-shrink-0">
              <i :class="[
                'mdi text-echoes-primary transition-transform duration-200',
                previewReferencesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              ]"></i>
            </div>
          </button>
          <div
            v-if="previewReferencesExpanded"
            class="p-3 bg-black/20 max-h-80 overflow-y-auto"
          >
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <div
                v-for="ref in task.references"
                :key="ref.id"
                class="rounded-xl border border-echoes-border-gray bg-echoes-background-gray/20 overflow-hidden"
              >
                <div
                  v-if="isImageReference(ref) && ref.url"
                  class="relative h-32 bg-black/50 border-b border-echoes-border-gray"
                >
                  <img :src="ref.url" :alt="ref.filename" class="w-full h-full object-contain" />
                </div>
                <div
                  v-else
                  class="h-32 bg-echoes-border-gray/20 border-b border-echoes-border-gray flex items-center justify-center"
                >
                  <i :class="[isImageReference(ref) ? 'mdi mdi-image-off-outline' : 'mdi mdi-file-document-outline', 'text-4xl text-white/30']"></i>
                </div>
                <div class="p-2.5">
                  <div class="flex items-start justify-between gap-2">
                    <p class="text-xs font-medium text-white truncate m-0">{{ ref.filename }}</p>
                    <a
                      v-if="ref.url"
                      :href="ref.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-[11px] text-echoes-primary hover:text-echoes-primary/80 whitespace-nowrap"
                    >
                      Open
                    </a>
                  </div>
                  <p class="text-[11px] text-white/50 mt-1 mb-0">
                    {{ formatReferenceType(ref) }}<span v-if="ref.size"> • {{ formatFileSize(ref.size) }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Answer Section -->
        <div class="border border-echoes-border-gray rounded-xl overflow-hidden">
          <button 
            @click="previewAnswerExpanded = !previewAnswerExpanded"
            class="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-all duration-200 cursor-pointer">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary flex-shrink-0">
              <i class="mdi mdi-check-circle-outline text-echoes-primary text-lg"></i>
            </div>
            <div class="flex-1 text-left">
              <h2 class="text-sm uppercase text-echoes-primary font-semibold tracking-wider">
                GOLDEN ANSWER
              </h2>
              <p class="text-xs text-white/50 mt-0.5">{{ previewAnswerExpanded ? 'Click to collapse' : 'Click to expand' }}</p>
            </div>
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary/50 bg-echoes-primary/10 flex-shrink-0">
              <i :class="[
                'mdi text-echoes-primary transition-transform duration-200',
                previewAnswerExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              ]"></i>
            </div>
          </button>
          <div v-if="previewAnswerExpanded" class="p-4 m-4 bg-echoes-background-gray/50 rounded-lg">
            <MarkdownRenderer :model-value="task.answer" :is-editing="false" :content-only="true" />
          </div>
        </div>

        <!-- Criteria Section -->
        <div v-if="(task.criteria || []).length > 0" class="border border-echoes-border-gray rounded-xl overflow-hidden">
          <button 
            @click="previewCriteriaExpanded = !previewCriteriaExpanded"
            class="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-all duration-200 cursor-pointer">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary flex-shrink-0">
              <i class="mdi mdi-format-list-bulleted text-echoes-primary text-lg"></i>
            </div>
            <div class="flex-1 text-left">
              <h2 class="text-sm uppercase text-echoes-primary font-semibold tracking-wider">
                GRADING RUBRIC
              </h2>
              <p class="text-xs text-white/50 mt-0.5">{{ previewCriteriaExpanded ? 'Click to collapse' : 'Click to expand' }}</p>
            </div>
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary/50 bg-echoes-primary/10 flex-shrink-0">
              <i :class="[
                'mdi text-echoes-primary transition-transform duration-200',
                previewCriteriaExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              ]"></i>
            </div>
          </button>
          <div v-if="previewCriteriaExpanded" class="p-4 bg-black/20">
            <div class="flex flex-col gap-4">
              <div v-for="(criterion, idx) in task.criteria" :key="criterion.id" 
                class="pb-4 border-b border-echoes-border-gray/50 last:border-b-0">
                <div class="flex items-start gap-4 mb-2">
                  <div class="flex items-center gap-3 flex-1 min-w-0">
                    <div class="flex items-center gap-2 min-w-0 flex-1">
                      <span class="text-white/90 text-sm font-semibold flex-shrink-0">Criterion {{ idx + 1 }}:</span>
                      <div class="criterion-title-preview line-clamp-1 overflow-hidden flex-1 min-w-0">
                        <MarkdownRenderer :model-value="criterion.name || 'Unnamed Criterion'" :is-editing="false" :content-only="true" />
                      </div>
                    </div>
                    <span class="text-white/40 flex-shrink-0">|</span>
                    <span class="text-white/90 text-sm flex-shrink-0">
                      <span class="font-semibold">Weight:</span> {{ criterion.weight || 0 }}%
                    </span>
                    <span class="text-white/40 flex-shrink-0">|</span>
                    <span class="text-white/90 text-sm flex-shrink-0">
                      <span class="font-semibold">Grader:</span> {{ formatCriterionGraderType(criterion) }}
                    </span>
                  </div>
                </div>
                
                <div v-if="criterion.semanticPrompt || criterion.description" 
                  class="mt-3 p-3 bg-echoes-background-gray/50 rounded-lg">
                  <MarkdownRenderer :model-value="criterion.semanticPrompt || criterion.description" :is-editing="false" :content-only="true" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Environment Section -->
        <div v-if="props.isAgentic" class="border border-echoes-border-gray rounded-xl overflow-hidden">
          <button
            @click="previewEnvironmentExpanded = !previewEnvironmentExpanded"
            class="w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-all duration-200 cursor-pointer"
          >
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary flex-shrink-0">
              <i class="mdi mdi-cog-outline text-echoes-primary text-lg"></i>
            </div>
            <div class="flex-1 text-left">
              <h2 class="text-sm uppercase text-echoes-primary font-semibold tracking-wider">ENVIRONMENT CONFIG</h2>
              <p class="text-xs text-white/50 mt-0.5">{{ previewEnvironmentExpanded ? 'Click to collapse' : 'Click to expand' }}</p>
            </div>
            <div class="flex items-center justify-center w-8 h-8 rounded-lg border border-echoes-primary/50 bg-echoes-primary/10 flex-shrink-0">
              <i :class="[
                'mdi text-echoes-primary transition-transform duration-200',
                previewEnvironmentExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
              ]"></i>
            </div>
          </button>
          <div v-if="previewEnvironmentExpanded" class="p-4 bg-black/20 space-y-4">
            <!-- Metadata -->
            <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Difficulty</div>
                <div class="text-sm text-white capitalize">{{ getMetadataSummary(task).difficulty }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Category</div>
                <div class="text-sm text-white">{{ getMetadataSummary(task).category }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3 lg:col-span-1 col-span-2">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Tags</div>
                <div class="text-sm text-white/90">{{ getMetadataSummary(task).tags }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Expert Time</div>
                <div class="text-sm text-white">{{ getMetadataSummary(task).expertTime }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Junior Time</div>
                <div class="text-sm text-white">{{ getMetadataSummary(task).juniorTime }}</div>
              </div>
            </div>

            <!-- Container: Dockerfile mode -->
            <div v-if="containerMode === 'dockerfile' && (task.environment?.dockerfile || '').trim()">
              <div class="flex items-center gap-2 mb-2">
                <div class="text-[10px] uppercase tracking-wide text-white/50">Container</div>
                <span class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">Custom Dockerfile</span>
              </div>
              <pre class="rounded-lg border border-echoes-border-gray bg-[#0d1117] p-3 text-xs text-green-300 font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap break-words m-0">{{ task.environment.dockerfile }}</pre>
            </div>

            <!-- Container: Simple mode -->
            <template v-else>
              <div class="grid grid-cols-1 gap-3">
                <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                  <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Base Image</div>
                  <div class="text-sm text-white font-mono break-all">{{ getEnvironment().base_image }}</div>
                </div>
              </div>
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                  <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">System Packages</div>
                  <div class="text-sm text-white/90 break-words">
                    {{ (getEnvironment().system_packages || []).length ? getEnvironment().system_packages.join(', ') : 'None' }}
                  </div>
                </div>
                <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                  <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Python Packages</div>
                  <div class="text-sm text-white/90 break-words">
                    {{ (getEnvironment().python_packages || []).length ? getEnvironment().python_packages.join(', ') : 'None' }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Resources (always shown) -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">CPUs</div>
                <div class="text-sm text-white">{{ getEnvironment().resources?.cpus ?? 1 }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Memory</div>
                <div class="text-sm text-white">{{ Math.round((getEnvironment().resources?.memory_mb ?? 2048) / 1024) }} GB</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Storage</div>
                <div class="text-sm text-white">{{ Math.round((getEnvironment().resources?.storage_mb ?? 10240) / 1024) }} GB</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">GPUs</div>
                <div class="text-sm text-white">{{ getEnvironment().resources?.gpus ?? 0 }}</div>
              </div>
            </div>

            <!-- Timeouts (always shown) -->
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Agent Timeout</div>
                <div class="text-sm text-white">{{ formatTimeoutHint(getEnvironment().timeouts?.agent_sec ?? 900) }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Verifier Timeout</div>
                <div class="text-sm text-white">{{ formatTimeoutHint(getEnvironment().timeouts?.verifier_sec ?? 900) }}</div>
              </div>
              <div class="rounded-lg border border-echoes-border-gray bg-echoes-background-gray/30 p-3">
                <div class="text-[10px] uppercase tracking-wide text-white/50 mb-1">Build Timeout</div>
                <div class="text-sm text-white">{{ formatTimeoutHint(getEnvironment().timeouts?.build_sec ?? 600) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div v-if="!props.isExampleMode" class="flex justify-center items-center gap-4 flex-shrink-0">
          <button 
            @click="emit('previousTask')"
            :disabled="isFirstTask"
            :class="[
              'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 bg-echoes-background-gray border border-echoes-border-gray',
              isFirstTask 
                ? 'text-white/30 cursor-not-allowed opacity-50' 
                : 'text-white hover:bg-echoes-border-gray/50 cursor-pointer'
            ]">
            <div class="flex items-center justify-center w-5 h-5 rounded-full border border-current">
              <i class="mdi mdi-chevron-left text-sm"></i>
            </div>
            <span>Previous Task</span>
          </button>
          
          <button 
            @click="emit('nextTask')"
            :disabled="isLastTask"
            :class="[
              'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 bg-echoes-background-gray border border-echoes-border-gray',
              isLastTask 
                ? 'text-white/30 cursor-not-allowed opacity-50' 
                : 'text-white hover:bg-echoes-border-gray/50 cursor-pointer'
            ]">
            <span>Next Task</span>
            <div class="flex items-center justify-center w-5 h-5 rounded-full border border-current">
              <i class="mdi mdi-chevron-right text-sm"></i>
            </div>
          </button>
        </div>
        </div>
      </div>
    </div>
  </template>
  
  <style scoped>
  .criterion-preview,
  .criterion-title-preview {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .criterion-preview :deep(.prose),
  .criterion-preview :deep(.content-only),
  .criterion-preview :deep(.preview-content),
  .criterion-preview :deep(p),
  .criterion-preview :deep(div),
  .criterion-title-preview :deep(.prose),
  .criterion-title-preview :deep(.content-only),
  .criterion-title-preview :deep(.preview-content),
  .criterion-title-preview :deep(p),
  .criterion-title-preview :deep(div) {
    display: inline;
    margin: 0;
    padding: 0;
  }
  
  .criterion-preview :deep(*) {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    line-height: 1.5;
  }
  
  .criterion-title-preview :deep(*) {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    line-height: 1.5;
  }
  </style>
